/** 只接受项目内短段落引用；无效列表整体丢弃，避免部分引用伪装为完整证据。 */
export function normalizeSourceReferences(value: unknown): string | null {
    if (typeof value !== 'string' || value.length > 100_000) return null;
    const refs = value.split(',').map(item => item.trim());
    if (!refs.length || refs.some(item => !/^f[1-9]\d{0,8}-p[1-9]\d{0,8}$/.test(item))) return null;
    return [...new Set(refs)].join(',');
}

export interface SourceReferenceTextSegment {
    kind: 'text' | 'reference';
    text: string;
}

const SOURCE_REFERENCE_IN_TEXT_PATTERN = /f[1-9]\d{0,8}-p[1-9]\d{0,8}/g;
const SOURCE_REFERENCE_IDENTIFIER_CHAR_PATTERN = /[\p{L}\p{N}_-]/u;
const SOURCE_REFERENCE_SKIP_SELECTOR = 'a, button, code, pre, textarea';

/** 从自然语言中提取独立的项目来源编码，同时原样保留标点和空白。 */
export function splitSourceReferenceText(value: string): SourceReferenceTextSegment[] {
    const segments: SourceReferenceTextSegment[] = [];
    let cursor = 0;
    for (const match of value.matchAll(SOURCE_REFERENCE_IN_TEXT_PATTERN)) {
        const index = match.index;
        const before = value[index - 1] || '';
        const after = value[index + match[0].length] || '';
        if (SOURCE_REFERENCE_IDENTIFIER_CHAR_PATTERN.test(before)
            || SOURCE_REFERENCE_IDENTIFIER_CHAR_PATTERN.test(after)) continue;
        if (index > cursor) segments.push({ kind: 'text', text: value.slice(cursor, index) });
        segments.push({ kind: 'reference', text: match[0] });
        cursor = index + match[0].length;
    }
    if (cursor < value.length || !segments.length) {
        segments.push({ kind: 'text', text: value.slice(cursor) });
    }
    return segments;
}

/**
 * 给已经清洗过的展示 HTML 增加站内来源链接。
 *
 * 只遍历文本节点，不会改写已有链接、代码块、按钮或编辑控件；新增 href 只包含
 * 通过严格格式校验的短编码，点击行为由上层只读渲染器统一接管。
 */
export function linkSourceReferencesInHtml(html: string): string {
    if (!html || typeof document === 'undefined') return html;
    const template = document.createElement('template');
    template.innerHTML = html;
    const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
    const candidates: Text[] = [];
    let current = walker.nextNode();
    while (current) {
        const parent = current.parentElement;
        if (parent && !parent.closest(SOURCE_REFERENCE_SKIP_SELECTOR)
            && splitSourceReferenceText(current.textContent || '').some(segment => segment.kind === 'reference')) {
            candidates.push(current as Text);
        }
        current = walker.nextNode();
    }
    candidates.forEach(node => {
        const fragment = document.createDocumentFragment();
        splitSourceReferenceText(node.textContent || '').forEach(segment => {
            if (segment.kind === 'text') {
                fragment.append(document.createTextNode(segment.text));
                return;
            }
            const link = document.createElement('a');
            link.setAttribute('href', `#source-reference=${segment.text}`);
            link.textContent = segment.text;
            fragment.append(link);
        });
        node.replaceWith(fragment);
    });
    return template.innerHTML;
}

export const SOURCE_REFERENCE_HTML_TAGS = new Set([
    'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'UL', 'OL', 'LI', 'BLOCKQUOTE',
    'PRE', 'TABLE', 'TR', 'TD', 'TH', 'DIV', 'HR', 'IMG',
]);

/** 对比引用值及其在文档结构中的归属；不把 DOM 序号解释为来源段落号。 */
export function sourceReferenceSignature(element: Element): string {
    const entries: Array<[number, string]> = [];
    [element, ...element.querySelectorAll('*')].forEach((node, index) => {
        const refs = normalizeSourceReferences(node.getAttribute('ref'));
        if (refs) entries.push([index, refs]);
    });
    return JSON.stringify(entries);
}
